<template>
  <TemplateIceBreakerGamesCategory
    v-if="kind === 'category'"
    v-bind="data"
  />
  <TemplateIceBreakerGamesModel
    v-else-if="kind === 'model'"
    v-bind="data"
  />
</template>

<script setup>
// Layout
definePageMeta({
  layout: 'default',
  middleware: ['check-if-the-ice-breaker-games-category-or-model-exists'],
})

// i18n
defineI18nRoute({
  locales: ['en'],
  paths: {
    en: '/ice-breaker-games/[slug]',
  },
})

// Composables
const { page } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const localePath = useLocalePath()
const { params } = useRoute()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()

// Async Data
const { data } = await useAsyncData(
  `page-ice-breaker-games-[slug]-${params.slug}-${locale.value}`,
  async () => {
    return Promise.all([
      $fetch(`/api/content/pages/ice-breaker-hub/${locale.value}`, { query: { preview: state.preview } }),
      $fetch(`/api/content/pages/ice-breaker-games-index/${locale.value}`, { query: { preview: state.preview } }),
      $fetch(`/api/content/pages/ice-breaker-games-list/${locale.value}`, { query: { preview: state.preview } }),
    ])
  },
  {
    transform: ([{ home, page: hub }, data, allIcebreakers]) => {
      const page = [
        ...data.allCategories.map(category => ({ ...category, type: 'category' })),
        ...allIcebreakers.map(model => ({ ...model, type: 'model' })),
      ].find(({ slug }) => slug === params.slug)

      return {
        ...data,
        allIcebreakers: allIcebreakers
          .map(({ id, _createdAt, _updatedAt, color, pattern, heroPopular, heroDuration, heroTitle, heroText, heroSubCategories, slug }) => ({
            id,
            color,
            duration: heroDuration,
            isPopular: heroPopular,
            pattern,
            title: heroTitle,
            text: heroText,
            tags: heroSubCategories.map(({ tag }) => tag),
            slug,
            updatedAt: _updatedAt || _createdAt,
          })),
        home,
        hub,
        page,
        parentPage: data.page,
      }
    },
  },
)

if (!data.value?.page) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

// Meta
const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

useSeoMeta({
  title: data.value.page.seoTitle,
  description: data.value.page.seoDescription,
  ogTitle: data.value.page.seoTitle,
  ogDescription: data.value.page.seoDescription,
  twitterTitle: data.value.page.seoTitle,
  twitterDescription: data.value.page.seoDescription,
})

useHead({
  link: head.value.link,
  meta: head.value.meta,
  script: [
    ...data.value.page.seoStructuredData.map(({ contentData }) => ({
      innerHTML: JSON.stringify({
        ...contentData,
      }),
      type: 'application/ld+json',
    })),
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.hub.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('ice-breaker')}`,
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': data.value.parentPage.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('ice-breaker-games')}`,
          },
          {
            '@type': 'ListItem',
            'position': 4,
            'name': data.value.page.trackingBreadcrumbTitle || (data.value.page.heroShortTitle || data.value.page.heroTitle),
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})

// Computed Properties
const kind = computed(() => {
  return data.value.page.type
})

// Lifecycle Kooks
onMounted(() => {
  page(data.value.page.trackName || data.value.page.heroShortTitle)
  setAvailableLanguages(['en'])
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath('ice-breaker'),
      name: data.value.hub.trackingBreadcrumbTitle,
    },
    {
      item: localePath('ice-breaker-games'),
      name: data.value.parentPage.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: data.value.page.trackingBreadcrumbTitle || (data.value.page.heroShortTitle || data.value.page.heroTitle),
    },
  ])
})
</script>
